/**
 * Pure H OS · Inventory · On-hand snapshot report.
 */
import { useCallback, useEffect, useState } from "react";
import { inventory, InvMeta, InvOnHandReport, InvWarehouse } from "../../services/api";

const VENTURE = "orvan";

export default function StockReport() {
  const [meta, setMeta] = useState<InvMeta | null>(null);
  const [warehouses, setWarehouses] = useState<InvWarehouse[]>([]);
  const [report, setReport] = useState<InvOnHandReport | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [kind, setKind] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await inventory.onHandReport(VENTURE, {
        warehouseId: warehouseId || undefined,
        kind: kind || undefined,
      });
      setReport(r);
    } finally {
      setLoading(false);
    }
  }, [warehouseId, kind]);

  useEffect(() => {
    inventory.meta().then(setMeta);
    inventory.listWarehouses(VENTURE, { includeInactive: true }).then(setWarehouses);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <section data-testid="stock-report">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--ph-ink-strong)" }}>On-hand snapshot</h2>
          <div className="text-xs text-slate-500 mt-1">Live view of every StockItem with positive quantity.</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="report-warehouse-filter"
            className="px-2 py-1.5 text-xs border rounded"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">All warehouses</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select
            data-testid="report-kind-filter"
            className="px-2 py-1.5 text-xs border rounded"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">All kinds</option>
            {(meta?.stockItemKinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <KPI label="Lots" value={String(report.lotCount)} testId="report-kpi-lots" />
            <KPI label="Total value (base)" value={`${report.baseCurrency || "—"} ${(report.totalValueBase || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} testId="report-kpi-value" />
            <KPI label="Warehouses" value={String(report.byWarehouse.length)} testId="report-kpi-warehouses" />
          </div>

          {report.byWarehouse.length > 0 && (
            <div className="mb-6" data-testid="report-by-warehouse">
              <h3 className="text-sm font-medium mb-2" style={{ color: "var(--ph-ink-strong)" }}>By warehouse</h3>
              <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                    <tr>
                      <th className="text-left px-3 py-2">Warehouse</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Lots</th>
                      <th className="text-right px-3 py-2">Total units</th>
                      <th className="text-right px-3 py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byWarehouse.map((g) => (
                      <tr key={g.warehouseId} data-testid={`report-wh-${g.warehouseId}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                        <td className="px-3 py-2">{g.warehouse?.name || g.warehouseId}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{g.warehouse?.type?.replace(/_/g, " ")}</td>
                        <td className="px-3 py-2 text-right">{g.lotCount}</td>
                        <td className="px-3 py-2 text-right">{g.totalUnits.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {report.baseCurrency} {g.totalValueBase.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <h3 className="text-sm font-medium mb-2" style={{ color: "var(--ph-ink-strong)" }}>Lots</h3>
          <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
            <table className="w-full text-sm" data-testid="report-lots-table">
              <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Lot</th>
                  <th className="text-left px-3 py-2">Warehouse</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Unit cost</th>
                  <th className="text-left px-3 py-2">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {report.stockItems.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">No stock on hand for the current filter.</td></tr>
                )}
                {report.stockItems.map((s) => (
                  <tr key={s.id} data-testid={`report-lot-${s.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium">{s.itemRef?.name || s.itemRefId}</div>
                      <div className="text-slate-400 font-mono">{s.itemRef?.docNumber || s.itemRef?.code || s.itemRefId}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{s.lotLabel || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{s.warehouse?.name}</td>
                    <td className="px-3 py-2 text-right font-medium">{s.quantity} {s.unit}</td>
                    <td className="px-3 py-2 text-right text-xs">{s.unitCost.baseCurrency} {s.unitCost.baseAmount?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{s.expiryAt || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {loading && <div className="text-center text-xs text-slate-400 py-6">Loading…</div>}
    </section>
  );
}

function KPI({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div data-testid={testId} className="p-4" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-1">{label}</div>
      <div className="text-xl font-serif" style={{ color: "var(--ph-ink-strong)" }}>{value}</div>
    </div>
  );
}
